import React from 'react';

function OptionGroup({ label, children }) {
    return (
        <div className="mb-6">
            <p className="text-xs font-semibold uppercase tracking-widest text-white/40 mb-2">{label}</p>
            {children}
        </div>
    );
}

function ToggleRow({ label, checked, onChange }) {
    return (
        <label className="flex items-center justify-between py-2 cursor-pointer">
            <span className="text-sm text-white/80">{label}</span>
            <button
                role="switch"
                aria-checked={checked}
                onClick={() => onChange(!checked)}
                className={`relative w-10 h-6 rounded-full transition-colors duration-200 focus:outline-none ${checked ? 'bg-purple-500' : 'bg-white/20'}`}
            >
                <span
                    className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full shadow transition-transform duration-200 ${checked ? 'translate-x-4' : 'translate-x-0'}`}
                />
            </button>
        </label>
    );
}

function ChipRow({ options, value, onChange }) {
    return (
        <div className="flex gap-2 flex-wrap">
            {options.map(opt => (
                <button
                    key={opt.value}
                    onClick={() => onChange(opt.value)}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                        value === opt.value
                            ? 'bg-purple-500 text-white'
                            : 'bg-white/10 text-white/70 hover:bg-white/20'
                    }`}
                >
                    {opt.label}
                </button>
            ))}
        </div>
    );
}

function ThemeChip({ themeKey, label, bg, active, onClick }) {
    return (
        <button
            onClick={() => onClick(themeKey)}
            className={`flex flex-col items-center gap-1.5 p-2 rounded-xl transition-all ${active ? 'ring-2 ring-purple-500 bg-white/10' : 'hover:bg-white/5'}`}
        >
            <div className="w-12 h-8 rounded-md shadow-inner" style={{ background: bg }} />
            <span className="text-xs text-white/70">{label}</span>
        </button>
    );
}

export default function SettingsPanel({ settings, onUpdate, onClose, mode }) {
    return (
        <>
            {/* Backdrop */}
            <div
                className="fixed inset-0 z-40"
                onClick={onClose}
            />

            {/* Panel */}
            <div className="fixed top-0 right-0 h-full w-72 bg-gray-950/95 backdrop-blur-xl z-50 flex flex-col shadow-2xl border-l border-white/10">
                {/* Header */}
                <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
                    <h2 className="text-white font-semibold text-base">Customize</h2>
                    <button
                        onClick={onClose}
                        className="text-white/50 hover:text-white transition w-7 h-7 flex items-center justify-center rounded-lg hover:bg-white/10"
                    >
                        ✕
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto px-5 py-5">

                    {/* Board section (only in board mode) */}
                    {mode === 'mosaic' && (
                        <>
                            <OptionGroup label="Background">
                                <div className="flex gap-3">
                                    <ThemeChip
                                        themeKey="cork"
                                        label="Cork"
                                        bg="linear-gradient(135deg, #c4955a, #9a6a30)"
                                        active={settings.theme === 'cork'}
                                        onClick={v => onUpdate('theme', v)}
                                    />
                                    <ThemeChip
                                        themeKey="dark"
                                        label="Dark"
                                        bg="linear-gradient(135deg, #1e293b, #0d1117)"
                                        active={settings.theme === 'dark'}
                                        onClick={v => onUpdate('theme', v)}
                                    />
                                    <ThemeChip
                                        themeKey="light"
                                        label="Light"
                                        bg="linear-gradient(135deg, #f5f0e8, #e8dfc8)"
                                        active={settings.theme === 'light'}
                                        onClick={v => onUpdate('theme', v)}
                                    />
                                </div>
                            </OptionGroup>

                            <OptionGroup label="Card size">
                                <ChipRow
                                    options={[
                                        { value: 'small',  label: 'Small'  },
                                        { value: 'medium', label: 'Medium' },
                                        { value: 'large',  label: 'Large'  },
                                    ]}
                                    value={settings.cardSize}
                                    onChange={v => onUpdate('cardSize', v)}
                                />
                            </OptionGroup>

                            <OptionGroup label="Rotation">
                                <ChipRow
                                    options={[
                                        { value: 'none',   label: 'Straight' },
                                        { value: 'subtle', label: 'Subtle'   },
                                        { value: 'wild',   label: 'Wild'     },
                                    ]}
                                    value={settings.rotation}
                                    onChange={v => onUpdate('rotation', v)}
                                />
                            </OptionGroup>
                        </>
                    )}

                    {/* Content — both modes */}
                    <OptionGroup label="Content">
                        <ToggleRow
                            label="Show captions"
                            checked={settings.showCaptions}
                            onChange={v => onUpdate('showCaptions', v)}
                        />
                        <ToggleRow
                            label="Show uploader names"
                            checked={settings.showNames}
                            onChange={v => onUpdate('showNames', v)}
                        />
                        <ToggleRow
                            label="Show QR code"
                            checked={settings.showQr}
                            onChange={v => onUpdate('showQr', v)}
                        />
                    </OptionGroup>

                    {/* Slideshow speed — cinematic mode only */}
                    {mode === 'cinematic' && (
                        <OptionGroup label="Slideshow speed">
                            <ChipRow
                                options={[
                                    { value: 5,  label: '5 s'  },
                                    { value: 10, label: '10 s' },
                                    { value: 20, label: '20 s' },
                                ]}
                                value={settings.slideDuration}
                                onChange={v => onUpdate('slideDuration', v)}
                            />
                        </OptionGroup>
                    )}

                </div>
            </div>
        </>
    );
}
